<template>
  <div class="x-table-page">
    <el-form
      class="x-form"
      :labelWidth="queryForm.labelWidth"
      :labelSuffix="queryForm.labelSuffix || ':'"
      :style="{ '--query-label-min-width': queryLabelMinWidth }"
      v-if="queryForm.formItems.length"
    >
      <el-row>
        <!-- 表单查询项 -->
        <template v-for="(item, index) in queryForm.formItems" :key="index">
          <!-- 项自定义 | 全局自定义 | 默认 -->
          <el-col
            :xl="item.xl || btnRow.xl || 6"
            :lg="item.lg || btnRow.lg || 6"
            :md="item.md || btnRow.md || 8"
            :sm="item.sm || btnRow.sm || 12"
            :xs="item.xs || btnRow.xs || 12"
            class="form-row"
          >
            <el-form-item
              :label="item.label"
              style="
                display: flex;
                align-items: center;
                height: 100%;
                width: 100%;
              "
              :label-width="item.labelWidth"
            >
              <!-- 文本输入框 -->
              <template v-if="item.type === 'input'">
                <el-input
                  @input="(value) => inputChange(value, item)"
                  :type="item.inputType || 'text'"
                  :placeholder="item.placeholder"
                  v-model="formData[item.key]"
                  clearable
                />
              </template>
              <!-- 下拉选择框 -->
              <template v-if="item.type === 'select'">
                <el-select
                  v-bind="item"
                  :placeholder="item.placeholder"
                  @change="() => search()"
                  v-model="formData[item.key]"
                  clearable
                  style="width: 100%"
                >
                  <el-option
                    v-for="option in item.options"
                    :label="$t(option.label)"
                    :value="option.value"
                    :key="option.value"
                  ></el-option>
                </el-select>
              </template>
              <!-- 日期段选择 -->
              <template
                v-if="
                  item.type === 'daterange' || item.type === 'datetimerange'
                "
              >
                <el-date-picker
                  v-model="formData[item.key]"
                  clearable
                  @change="(time) => daterangeChange(time, item)"
                  :type="item.type"
                  range-separator="-"
                  :start-placeholder="item.startPlaceholder"
                  :end-placeholder="item.endPlaceholder"
                />
              </template>
              <!-- 日期点选择 -->
              <template v-if="item.type === 'datepicker'">
                <el-date-picker
                  v-model="formData[item.key]"
                  @change="(time) => datepickerChange(time, item)"
                  style="width: 100%"
                  clearable
                  :placeholder="$(item.placeholder)"
                />
              </template>
              <!-- 自定义插槽区域 -->
              <template v-if="item.scopedSlots">
                <slot :name="item.scopedSlots"></slot>
              </template>
            </el-form-item>
          </el-col>
        </template>
        <!-- 查询、重置按钮 -->
        <el-col
          :xl="btnRow.xl || 6"
          :lg="btnRow.lg || 6"
          :md="btnRow.md || 8"
          :sm="btnRow.sm || 12"
          :xs="btnRow.xs || 12"
          class="form-btns"
        >
          <div class="btn-box" v-if="showQuery || showReset">
            <template v-if="queryForm.formItems.length">
              <el-button v-if="showQuery" type="primary" @click="search">
                {{ $t("commonComponents.search") }}
              </el-button>
              <el-button v-if="showReset" @click="reset">
                {{ $t("commonComponents.reset") }}
              </el-button>
            </template>
          </div>
          <slot name="formFn"></slot>
        </el-col>
      </el-row>
    </el-form>
    <slot name="tableTop"></slot>
    <!-- 表格 -->
    <div class="x-table">
      <el-table
        :data="tableData"
        stripe
        ref="tableRef"
        v-loading="loading"
        :header-cell-style="{ background: '#FAFAFA' }"
        v-bind="$attrs"
        @current-change="tableChange"
        @sort-change="sortChange"
      >
        <template v-for="(item, index) in columns" :key="index">
          <el-table-column
            v-bind="item"
            :minWidth="
              $t('common.mode') === 'en'
                ? item.minWidthEn || item.minWidth
                : item.minWidth
            "
            :label="$t(`${item.label}`)"
          >
            <template #default="scope" v-if="item.scopedSlots">
              <slot :name="item.scopedSlots" :row="scope.row"></slot>
            </template>
            <template #header="scope">
              <slot :name="item.slotTitle" :column="scope.column" :tooltipContent="item.tooltipContent" :index="scope.index"></slot>
            </template>
          </el-table-column>
        </template>

        <template #empty>
          <el-row justify="center">
            <img src="@/assets/images/empty-bg.png" class="empty-png" alt="" />
          </el-row>
          <slot name="empty">
            <div class="empty">{{ $t("commonComponents.noData") }}</div>
          </slot>
        </template>
      </el-table>
      <div class="pagination">
        <el-pagination
          v-model:current-page="pageData.page"
          v-model:page-size="pageData.size"
          :page-sizes="[10, 20, 30, 50, 80, 100]"
          :layout="pagPayout"
          :total="total"
          :hide-on-single-page="hideOnSinglePage"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :size="pagSize"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import dayjs from "dayjs";
import { ElMessage } from "element-plus";
import { vuexForm } from "./vuex";
import { debounce, getTextWidth } from "@/utils/tools.js";
import { computed, ref, onMounted, onUnmounted, watch, unref, nextTick } from "vue";
import { $t } from "@/i18n";

const props = defineProps({
  vuexNamespace: {
    type: String,
    default: "",
  },
  queryForm: {
    type: Object,
    default: {
      formItems: [],
    },
  },
  http: {
    type: Function,
  },
  columns: {
    type: Array,
  },
  autoRequest: {
    type: Boolean,
    default: true,
  },
  /* 是否显示查询按钮 */
  showQuery: {
    type: Boolean,
    default: true,
  },
  /* 是否展示重置按钮 */
  showReset: {
    type: Boolean,
    default: true,
  },
  /* 定制 pagination layout，默认全部都有 */
  pagPayout: {
    type: String,
    default: `total, sizes, prev, pager, next, jumper`,
  },
  /* 分支只有一页时是否隐藏 */
  hideOnSinglePage: {
    type: Boolean,
    default: false,
  },
  /* 定制 pagination 尺寸 */
  pagSize: {
    type: String,
    default: "default",
  },
  btnRow: {
    type: Object,
    default: {
      xl: 6,
      lg: 6,
      md: 8,
      sm: 12,
      xs: 12,
    },
  },
  // 点击重置按钮是否刷新列表
  resetRefresh: {
    type: Boolean,
    default: true,
  },
});

const queryLabelMinWidth = computed(() =>
  $t("common.mode") === "zh-cn" ? "72px" : "140px",
);
const emit = defineEmits(["reset", "result", "tableChange", "selectSearch"]);
const inputChange = debounce((value, item) => {
  if (item?.inputType || item.maxlength) {
    formData.value[item.key] = value.slice(0, item.maxlength || 19);
  }
  search();
}, 600);
const formData = ref({}),
  loading = ref(false),
  tableRef = ref(),
  pageData = ref({ page: 1, size: 10 }),
  total = ref(0),
  tableData = ref([]),
  sort_type = ref(null),
  order_by = ref(null);

onMounted(() => {
  queryWidthCompute()
  if (props.vuexNamespace) {
    pageData.value.page = vuexForm.value[props.vuexNamespace]?.page || 1;
    pageData.value.size = vuexForm.value[props.vuexNamespace]?.size || 10;
    formData.value = { ...vuexForm.value[props.vuexNamespace] };
    delete formData.value.page;
    delete formData.value.size;
  }
  if (props.autoRequest) {
    defaultDataHandle();
    getList();
  }
});

onUnmounted(() => {
  vuexForm.value[props.vuexNamespace] = formData.value;
  vuexForm.value[props.vuexNamespace].page = pageData.value.page;
  vuexForm.value[props.vuexNamespace].size = pageData.value.size;
});

/**
 * 查询表文本宽度自动计算
 */
const queryWidthCompute = (length = 4) => {
  let queryWidth = new Array(length).fill(0);
  props.queryForm.formItems.forEach((item, index) => {
    queryWidth[index % queryWidth.length] =
      queryWidth[index % queryWidth.length] > getTextWidth(item.label)
        ? queryWidth[index % queryWidth.length]
        : getTextWidth(item.label);
  });
  props.queryForm.formItems.forEach((item, index) => {
    if (item.labelWidth !== undefined && item.labelWidth !== null && item.labelWidth !== '') return
    item.labelWidth = item.label && item.label != "" ? queryWidth[index % queryWidth.length] + 20: 0
  })
}

/* 表格 change 事件 */
const tableChange = (val) => {
  emit("tableChange", val);
};

// 排序
const sortChange = (val) => {
  if (val.order === "ascending") {
    //升序:1 越来越大
    sort_type.value = 1;
    order_by.value = val.prop;
  } else if (val.order === "descending") {
    //降序：0越来越小
    sort_type.value = 0;
    order_by.value = val.prop;
  } else {
    sort_type.value = null;
    order_by.value = null;
  }
  getList();
};

// 默认数据处理
function defaultDataHandle() {
  props.queryForm.formItems.forEach((item) => {
    if (
      (item.type === "daterange" || item.type === "datetimerange") &&
      item.config.defaultDay >= 0
    ) {
      formData.value[item.key] = [
        dayjs()
          .subtract(item.config.defaultDay, "day")
          .format("YYYY-MM-DD 00:00:00"),
        dayjs().format("YYYY-MM-DD 23:59:59"),
      ];
    }
  });
}

function getHttpParams() {
  const {
    queryForm: { formItems },
  } = props;
  const data = {};

  // 需要特殊处理的字段key的集合
  const specialFieldsMap = new Map();
  formItems.forEach((item) => {
    if (item.type === "daterange" || item.type === "datetimerange") {
      specialFieldsMap.set(item.key, item);
    }
  });

  // 处理表单数据
  Object.entries(formData.value).forEach(([key, value]) => {
    if (value == null || value === "") return;

    const dateRangeItem = specialFieldsMap.get(key);
    if (dateRangeItem) {
      const { startField, endField, formatt } = dateRangeItem.config;

      // 确保日期值有效
      if (value[0] && value[1]) {
        if (dateRangeItem.type === "daterange") {
          data[startField] = dayjs(value[0]).startOf("day").format(formatt);
          data[endField] = dayjs(value[1]).endOf("day").format(formatt);
        } else if (dateRangeItem.type === "datetimerange") {
          data[startField] = dayjs(value[0]).format(formatt);
          data[endField] = dayjs(value[1]).format(formatt);
        }
      }
      return;
    }
    const formItemsConfig = formItems.find(item => item.key == key)
    if (formItemsConfig) {
      // 多选数组按 joinChar 拼接
      if (Array.isArray(value) && formItemsConfig.joinChar) {
        value = value.join(formItemsConfig.joinChar)
      }
      // 数组类型或者超长数组强制截取最多18
      if ((formItemsConfig.type === 'input' && formItemsConfig.inputType === 'number') || formItemsConfig.maxlength) {
        value = value.slice(0, formItemsConfig.maxlength || 19)
      }
    }
    data[key] = value;
  });

  // 处理排序参数
  if (sort_type.value != null) {
    data.sort_type = sort_type.value;
  }
  if (order_by.value != null) {
    data.order_by = order_by.value;
  }

  return data;
}

//获取数据
async function getList() {
  loading.value = true;
  const { page, size } = pageData.value;
  const { result } = await props.http({ page, size, ...getHttpParams() });
  if (result) {
    emit("result", result);
    tableData.value = result.data.rows;
    total.value = result.data.total * 1;
  }
  loading.value = false;
}
//时间框改变
function daterangeChange(time, item) {
  const { maxInterval, startField, endField, formatt } = item.config;
  if (!time) {
    search();
    return;
  }
  //判断最多查询天数间隔限制
  if (maxInterval) {
    const start = dayjs(time[0]);
    const end = dayjs(time[1]);
    if (end.diff(start, "day") >= maxInterval) {
      ElMessage.error($t("commonComponents.maximumDays", [maxInterval]));
      delete formData.value[item.key];
      return;
    }
  }
  search();
}
//查询
function search() {
  pageInit();
  getList();
}
//重置
function reset() {
  emit("reset");
  for (const key in formData.value) {
    formData.value[key] = null;
  }
  defaultDataHandle();
  pageInit();
  getList();
}
//page初始化
function pageInit() {
  pageData.value.page = 1;
  pageData.value.size = 10;
}
function handleSizeChange(value) {
  pageData.value.size = value;
  getList();
}
function handleCurrentChange(value) {
  pageData.value.page = value;
  getList();
}
function refresh(isToOneFlag = false) {
  if (isToOneFlag) {
    pageInit();
  }
  getList();
}

function tableFn(fnName) {
  const args = [...arguments];
  return unref(tableRef)[fnName](...args.slice(1));
}

/* 更新 table 源数据 */
/* 
  rows 需要更新的行数据
  maps 需要更新的字段,例如 { id: 0 }, id 所有指定的 row 更新为 0
*/
function updateDataSource(rows, maps = {}) {
  rows.forEach((v) => {
    let findOne = tableData.value.find((vv) => vv.id == v.id);
    if (findOne) {
      Object.entries(maps).forEach(([key, value]) => (findOne[key] = value));
    }
  });
}

function setHttpParams(params) {
  Object.entries(params).forEach(([key, value]) => {
    formData.value[key] = value;
  });
}

/* 
  向外导出的方法和属性
*/
defineExpose({
  refresh,
  getHttpParams,
  tableFn,
  reset /* 没有 reset 按钮时刷新 */,
  updateDataSource,
  setHttpParams,
  getTableData() {
    return tableData.value;
  },
});
</script>

<style lang="scss" scoped>
.x-table-page {
  .x-form {
    padding-top: 16px;

    .form-row {
      padding-right: 30px;
    }
    @media (max-width: 1550px) {
      .form-row {
        max-width: 33%;
        flex: 0 0 33%;
      }
    }
    @media (max-width: 1330px) {
      .form-row {
        padding-right: 0px;
      }
    }

    .el-form-item {
      :deep(.el-form-item__label) {
        font-size: 13px;
        font-weight: 400;
        min-width: var(--query-label-min-width);
        color: #151515;
      }
    }
    .form-btns {
      display: flex;
      align-items: center;
    }
    .btn-box {
      display: flex;
      height: 56px;
      align-items: center;
      margin-right: 12px;
      // .el-button {
      //   padding: 0 24px;
      //   height: 36px;
      // }
      // :deep(.el-button) {
      //   padding: 0 24px;
      //   height: 36px;
      // }
    }
  }

  .x-table {
    margin-top: 16px;
    // padding: 0 24px 0 0;

    .el-table .cell {
      color: #d834da !important;
    }
  }
}

.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  padding-bottom: 16px;
}
</style>

<style lang="scss">
.x-table-page {
  // 表格统一按钮样式
  .el-button {
    padding: 0 24px;
    height: 36px;
  }

  .x-form {
    .el-input,
    .el-date-editor,
    .el-select__wrapper {
      height: 36px;
    }
  }

  thead {
    height: 54px;
    background-color: #fafafa;
    .cell {
      color: #151515;
      font-weight: 600;
    }
  }

  tbody {
    .cell {
      color: #151515;
      font-weight: 400;
    }

    .el-table__row {
      height: 52px !important;
    }
  }
  .empty {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .empty-png {
    width: 120px;
    height: 100px;
    margin: 60px 0 16px;
  }
  .pagination {
    .el-pager {
      .number.is-active {
        border: 1px solid var(--el-color-primary);
        border-radius: 4px 4px 4px 4px;
      }
    }
  }
}

.custom-width {
  min-width: 360px;
}
</style>
